<script setup lang="ts">
import type { ManageCookiesSetting } from '@/components/layout/ManageCookies/ManageCookiesCompose'
import * as Sentry from '@sentry/vue'
import { addGtag } from 'vue-gtag'
import Logo from '@/assets/logo-green.svg'
import { gtagReady, readManageCookies, showBar, showManager } from '@/components/layout/ManageCookies/ManageCookiesCompose'

const { nextLoopEvent } = utilsPlugin
const { t, locale } = useI18n()

const manageCookies = ref<ManageCookiesSetting>({ showBar: false, optional: { ga: false } })

onMounted(() => {
  const settings = readManageCookies()
  if (settings?.optional) {
    manageCookies.value.optional = { ...manageCookies.value.optional, ...settings.optional }
  }
  if (settings?.showBar !== false || settings?.optional?.ga === undefined) {
    nextLoopEvent(400).then(() => {
      showBar.value = true
    })
  }

  if (gaUtils.canLoadGtag() && settings?.optional?.ga) {
    // gtagReady is set from the 'script:loaded' hook in routerInitPlugin.ts, not here — this
    // promise resolves even when the underlying script failed to load (see that hook's comment).
    addGtag().catch((err) => {
      Sentry.captureException(err, { tags: { source: 'addGtag' } })
    })
  }
  if (settings?.optional?.ga !== undefined) {
    gaUtils.onConsentDecision(Boolean(settings.optional.ga)) // decision from a previous visit - A/B session id source
  }
})

function close() {
  showManager.value = false
  localStorage?.setItem?.('manageCookies', JSON.stringify(manageCookies.value))
  if (manageCookies.value?.optional?.ga) {
    if (gtagReady.value) {
      utilsPlugin.getApp()?.$gtag?.optIn()
      gaUtils.resumeLaunchTracking() // after optIn - siteGtag() is non-null even when opted out, events must not hit disabled GA
    } else if (gaUtils.canLoadGtag()) { // dev mode without VITE_DEBUG_GA='on': consent is stored, GA is never loaded
      gaUtils.resumeLaunchTracking() // before addGtag - its 'script:loaded' -> onReadyVueGtag must see tracking enabled
      addGtag().catch((err) => {
        Sentry.captureException(err, { tags: { source: 'addGtag' } })
      })
    }
  } else {
    utilsPlugin.getApp()?.$gtag?.optOut()
    gaUtils.clearLaunchMarkers()
  }
  gaUtils.onConsentDecision(Boolean(manageCookies.value?.optional?.ga))
}
function saveSelection(all = false) {
  if (all) {
    Object.keys(manageCookies.value.optional).forEach((key) => {
      manageCookies.value.optional[key] = true
    })
  }
  showBar.value = manageCookies.value.showBar = false
  close()
}
watch(
  () => showManager.value,
  () => {
    if (showManager.value) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }
)
</script>

<template>
  <transition name="accordion-cookies">
    <div
      v-if="showBar"
      id="manage-cookies-bar"
      class="fixed bottom-0 left-0 right-0 grid bg-black/90 text-white text-base transition-all duration-500 ease-in z-50"
    >
      <div class="overflow-hidden">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-[15%] gap-y-10 px-4 py-10 sm:p-[5vw] items-center">
          <div class="text-center flex justify-center md:justify-end">
            <div class="inline-block">
              <div>{{ t('cookies.bar.message') }}</div>
              <a
                :href="`https://gamesture.com/privacy-policy?flang=${locale}`"
                target="_blank"
                rel="noopener noreferrer"
                class="border-b-2 border-gs-bg px-0.5 hover:text-gs-main-color cursor-pointer whitespace-nowrap"
              >
                {{ t('cookies.bar.learnMore') }} <icon-ify
                  icon="mingcute:arrows-right-fill"
                  class="w-2.5 inline -ml-1"
                />
              </a>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row sm:flex-wrap justify-center md:justify-center gap-x-8 gap-y-4">
            <div class="inline-flex justify-center">
              <button
                class="bg-gs-main-color px-4 py-3 rounded-sm font-semibold active:opacity-90 cursor-pointer hover:opacity-85"
                @click="saveSelection(true)"
              >
                {{ t('cookies.bar.acceptAll') }}
              </button>
            </div>
            <button
              class="text-gs-main-color font-semibold active:opacity-90 cursor-pointer hover:opacity-85"
              @click="saveSelection(false)"
            >
              {{ t('cookies.bar.dontAccept') }}
            </button>
            <button
              class="text-gs-main-color font-semibold active:opacity-90 cursor-pointer hover:opacity-85"
              @click="showManager = true"
            >
              {{ t('cookies.bar.manage') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  <transition name="toggle">
    <div
      v-if="showManager"
      class="manager fixed bg-black/90 overflow-hidden z-50"
    >
      <div class="h-dvh flex justify-center items-center big:items-start big:mt-[15vh] p-4">
        <div class="overflow-y-auto max-h-full min-w-10 max-w-full">
          <div class="rounded-2xl bg-white max-w-xl overflow-hidden border-2 border-cyan-800 border-t-8 border-b-8">
            <div class="border-b border-b-cyan-800 px-4 pt-4 pb-2 big:pl-8 relative">
              <div class="text-cyan-700 pb-1 flex justify-between items-center big:items-start">
                <div>
                  <Logo class="w-8 big:w-10" />
                </div>
                <button
                  class="text-2xl transition hover:rotate-90"
                  @click="showManager = false"
                >
                  <icon-ify
                    icon="material-symbols:close"
                    class=""
                  />
                </button>
              </div>
            </div>
            <div class="p-4 big:p-8">
              <h1 class="uppercase font-bold text-xl tracking-wide">
                {{ t('cookies.manager.title') }}
              </h1>
              <div class="mt-5 text-sm">
                <div class="flex justify-between items-center">
                  <h2 class="font-semibold text-lg">
                    {{ t('cookies.manager.necessary.title') }}
                  </h2>
                  <div class="text-gs-main-color">
                    {{ t('cookies.manager.necessary.alwaysActive') }}
                  </div>
                </div>
                <div class="mb-2 text-xs">
                  <div class="max-w-[80%]">
                    {{ t('cookies.manager.necessary.description') }}
                  </div>
                </div>
              </div>
              <div class="mt-5 text-sm">
                <div class="flex justify-between items-center">
                  <h2 class="font-semibold text-lg">
                    {{ t('cookies.manager.optional.title') }}
                  </h2>
                  <div class="text-gs-main-color">
                    {{ t('cookies.manager.optional.label') }}
                  </div>
                </div>
                <div class="mb-2 pt-2">
                  <div class="flex justify-between items-start gap-2">
                    <div class="grow max-w-[80%]">
                      <div class="font-medium text-gs-bg">
                        {{ t('cookies.manager.optional.analyticalTitle') }}
                      </div>
                      <div class="text-xs">
                        {{ t('cookies.manager.optional.analyticalDescription') }}
                      </div>
                    </div>
                    <div>
                      <el-switch
                        v-model="manageCookies.optional.ga"
                        inline-prompt
                        :active-text="`\u00a0${t('cookies.manager.on')}\u00a0`"
                        :inactive-text="`\u00a0${t('cookies.manager.off')}\u00a0`"
                        style="--el-switch-on-color: #2d7c8a; --el-switch-off-color: #9ea0a1"
                        size="large"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-5 text-sm">
                <a
                  :href="`https://gamesture.com/privacy-policy?flang=${locale}`"
                  class="relative text-cyan-700 tracking-wide underline-offset-2 px-0.5 hover:text-cyan-800 active:opacity-90"
                  rel="nofollow"
                  target="_blank"
                  @click="showManager = false"
                >
                  {{ t('cookies.manager.readMore') }} <icon-ify
                    icon="mingcute:arrows-right-fill"
                    class="w-2.5 inline -ml-1"
                  />
                </a>
              </div>
            </div>
            <div class="border-t border-t-cyan-800 px-4 pt-4 pb-3">
              <div class="flex justify-end">
                <div class="inline-flex justify-center text-white gap-3">
                  <button
                    class="border border-gs-main-color text-gs-main-color px-2.5 py-1.5 rounded-sm font-medium hover:text-gs-main-color hover:bg-gs-main-color/5 active:opacity-90"
                    @click="saveSelection(false)"
                  >
                    {{ t('cookies.manager.confirmChoice') }}
                  </button>
                  <button
                    class="bg-gs-main-color px-2.5 py-1.5 rounded-sm font-medium hover:text-cyan-50 active:opacity-90 cursor-pointer"
                    @click="saveSelection(true)"
                  >
                    {{ t('cookies.manager.acceptAll') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="postcss">
.accordion-cookies-enter-active,
.accordion-cookies-leave-active {
  grid-template-rows: 1fr;
}

.accordion-cookies-enter-from,
.accordion-cookies-leave-to {
  grid-template-rows: 0fr;
}

.manager {
  height: 100vh;
  width: 100vw;
  top: 0;
}

.toggle-enter-active {
  animation: toggle-in ease-in 0.5s;

  > * {
    opacity: 1;
  }
}

.toggle-leave-active {
  animation: toggle-in ease-in 0.5s reverse;

  > * {
    transition-property: opacity;
    transition-duration: 150ms;
    opacity: 0;
  }
}

@keyframes toggle-in {
  0% {
    height: 0;
    top: 50%;
  }

  100% {
    height: 100dvh;
    top: 0;
  }
}

a.relative {
  &::before {
    content: ' ';
    display: block;
    background: currentcolor;
    height: 2px;
    width: 0;
    transition: width;
    transition-timing-function: ease-in;
    transition-duration: 300ms;
    position: absolute;
    bottom: -1px;
    left: 0;
  }

  &:hover {
    &::before {
      width: 100%;
    }
  }
}
</style>
